import * as React from "react";
import { AlertCircle, Clock, Loader2, MoonStar } from "lucide-react";
import {
  Button,
  Input,
  Label,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Switch,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  SLUG_RULES,
  boundedNameSchema,
  deriveSlug,
  fieldError,
  slugProblemFor,
} from "@eduviaos/shared";
import {
  WEEKDAYS,
  WEEKDAY_SHORT,
  toTimeInput,
  useCreateSeatSlot,
  useUpdateSeatSlot,
  type CreateSeatSlotInput,
  type SeatSlot,
  type SeatSlotWeekday,
  type UpdateSeatSlotInput,
} from "@/lib/seatSlots/api";

/**
 * Phase SC-21d — create / edit one shift (seat slot).
 *
 * The key is what bookings store, so it is fixed once created; only the
 * label, hours, days, date range and order are editable. Overnight is
 * derived from the hours (end at or before start) rather than asked for,
 * matching the BE window rule. Edit mode PATCHes only changed fields; the
 * hours travel together so the BE validates the window as a whole.
 */

const LABEL_MAX = 80;
const DUPLICATE_LABEL_MESSAGE = "Another shift already has this name. Choose a different name.";
const labelSchema = boundedNameSchema(LABEL_MAX, "Shift name");

interface DraftState {
  label: string;
  start: string;
  end: string;
  days: Set<SeatSlotWeekday>;
  effectiveFrom: string;
  effectiveUntil: string;
  displayOrder: string;
  isActive: boolean;
}

function draftFrom(existing: SeatSlot | null | undefined, nextOrder: number): DraftState {
  if (!existing) {
    return {
      label: "",
      start: "09:00",
      end: "13:00",
      days: new Set(WEEKDAYS),
      effectiveFrom: "",
      effectiveUntil: "",
      displayOrder: String(nextOrder),
      isActive: true,
    };
  }
  return {
    label: existing.label,
    start: toTimeInput(existing.start_time),
    end: toTimeInput(existing.end_time),
    days: new Set(existing.days_of_week),
    effectiveFrom: existing.effective_from ?? "",
    effectiveUntil: existing.effective_until ?? "",
    displayOrder: String(existing.display_order),
    isActive: existing.is_active,
  };
}

function orderedDays(days: Set<SeatSlotWeekday>): SeatSlotWeekday[] {
  return WEEKDAYS.filter((d) => days.has(d));
}

function buildUpdate(draft: DraftState, existing: SeatSlot): UpdateSeatSlotInput {
  const body: UpdateSeatSlotInput = {};
  const label = draft.label.trim();
  if (label !== existing.label) body.label = label;

  const spans = draft.end <= draft.start;
  if (
    draft.start !== toTimeInput(existing.start_time) ||
    draft.end !== toTimeInput(existing.end_time) ||
    spans !== existing.spans_midnight
  ) {
    body.start_time = draft.start;
    body.end_time = draft.end;
    body.spans_midnight = spans;
  }

  const days = orderedDays(draft.days);
  const sameDays =
    days.length === existing.days_of_week.length &&
    days.every((d) => existing.days_of_week.includes(d));
  if (!sameDays) body.days_of_week = days;

  const from = draft.effectiveFrom || null;
  const until = draft.effectiveUntil || null;
  if (from !== existing.effective_from || until !== existing.effective_until) {
    body.effective_from = from;
    body.effective_until = until;
  }

  const order = Math.trunc(Number(draft.displayOrder));
  if (order !== existing.display_order) body.display_order = order;
  if (draft.isActive !== existing.is_active) body.is_active = draft.isActive;
  return body;
}

function isDuplicateKeyError(err: unknown): boolean {
  return err instanceof ApiError && err.details?.code === "seat.slot_key_exists";
}

function friendlyError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return "You don't have permission to manage shifts. Ask an admin to grant Manage zones.";
    }
    return err.message || "Couldn't save the shift. Please retry.";
  }
  return err instanceof Error ? err.message : "Couldn't save the shift. Please retry.";
}

export interface SeatSlotEditorSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existing?: SeatSlot | null;
  nextDisplayOrder: number;
  /** Keys of the shifts already listed, so a duplicate name is caught before the round-trip. */
  existingKeys?: readonly string[];
}

export function SeatSlotEditorSheet({
  open,
  onOpenChange,
  existing,
  nextDisplayOrder,
  existingKeys,
}: SeatSlotEditorSheetProps) {
  const isEdit = Boolean(existing);
  const [draft, setDraft] = React.useState<DraftState>(() => draftFrom(existing, nextDisplayOrder));
  const [showLabelError, setShowLabelError] = React.useState(false);
  const [serverLabelError, setServerLabelError] = React.useState<string | null>(null);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setDraft(draftFrom(existing, nextDisplayOrder));
      setShowLabelError(false);
      setServerLabelError(null);
      setSubmitError(null);
    }
  }, [open, existing, nextDisplayOrder]);

  const createMutation = useCreateSeatSlot();
  const updateMutation = useUpdateSeatSlot();
  const submitting = createMutation.isPending || updateMutation.isPending;

  const spansMidnight = draft.end <= draft.start;
  const label = draft.label.trim();
  const order = Number(draft.displayOrder);
  const derivedKey = isEdit ? "" : deriveSlug(draft.label, SLUG_RULES.seatSlotKey);
  const labelError =
    serverLabelError ??
    fieldError(labelSchema, draft.label) ??
    (isEdit
      ? null
      : slugProblemFor(derivedKey, SLUG_RULES.seatSlotKey, {
          noun: "shift",
          taken: existingKeys,
        }));
  const visibleLabelError =
    showLabelError || draft.label.length > 0 || serverLabelError ? labelError : null;
  const rangeValid =
    !draft.effectiveFrom || !draft.effectiveUntil || draft.effectiveFrom <= draft.effectiveUntil;
  const canSubmit =
    !labelError &&
    draft.days.size > 0 &&
    Boolean(draft.start && draft.end) &&
    draft.displayOrder.trim() !== "" &&
    Number.isInteger(order) &&
    order >= 0 &&
    rangeValid &&
    !submitting;

  function set<K extends keyof DraftState>(key: K, value: DraftState[K]) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  function toggleDay(day: SeatSlotWeekday) {
    setDraft((prev) => {
      const days = new Set(prev.days);
      if (days.has(day)) days.delete(day);
      else days.add(day);
      return { ...prev, days };
    });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    setShowLabelError(true);
    if (!canSubmit) return;
    try {
      if (existing) {
        const body = buildUpdate(draft, existing);
        if (Object.keys(body).length === 0) {
          onOpenChange(false);
          return;
        }
        const saved = await updateMutation.mutateAsync({ id: existing.id, body });
        toast.success(`Saved ${saved.label}.`);
      } else {
        const body: CreateSeatSlotInput = {
          key: derivedKey,
          label,
          start_time: draft.start,
          end_time: draft.end,
          spans_midnight: spansMidnight,
          days_of_week: orderedDays(draft.days),
          effective_from: draft.effectiveFrom || null,
          effective_until: draft.effectiveUntil || null,
          display_order: order,
        };
        const saved = await createMutation.mutateAsync(body);
        toast.success(`Added shift ${saved.label}.`);
      }
      onOpenChange(false);
    } catch (err) {
      if (isDuplicateKeyError(err)) {
        setServerLabelError(DUPLICATE_LABEL_MESSAGE);
        return;
      }
      setSubmitError(friendlyError(err));
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[480px]"
        data-testid="seat-slot-editor-sheet"
      >
        <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
          <div className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
            >
              <Clock className="size-5" />
            </span>
            <div className="flex-1">
              <SheetTitle className="tracking-tight">
                {isEdit ? "Edit shift" : "Add shift"}
              </SheetTitle>
              <SheetDescription className="mt-1 text-xs">
                {isEdit
                  ? "Learners see the new hours on their next booking. Past bookings keep their shift."
                  : "A window members can book a seat for, like Morning or Night owl."}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
            {submitError ? (
              <div
                role="alert"
                className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
              >
                <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                <span className="flex-1">{submitError}</span>
              </div>
            ) : null}

            <Field label="Shift name" htmlFor="slot-label" required error={visibleLabelError}>
              <Input
                id="slot-label"
                value={draft.label}
                maxLength={LABEL_MAX}
                required
                autoFocus
                placeholder="Morning"
                aria-invalid={visibleLabelError ? true : undefined}
                aria-describedby={visibleLabelError ? "slot-label-error" : undefined}
                onBlur={() => setShowLabelError(true)}
                onChange={(e) => {
                  setServerLabelError(null);
                  set("label", e.target.value);
                }}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Starts" htmlFor="slot-start" required>
                <Input
                  id="slot-start"
                  type="time"
                  value={draft.start}
                  required
                  className="tabular-nums"
                  onChange={(e) => set("start", e.target.value)}
                />
              </Field>
              <Field label="Ends" htmlFor="slot-end" required>
                <Input
                  id="slot-end"
                  type="time"
                  value={draft.end}
                  required
                  className="tabular-nums"
                  onChange={(e) => set("end", e.target.value)}
                />
              </Field>
            </div>
            {spansMidnight ? (
              <p className="text-muted-foreground -mt-2 flex items-center gap-1.5 text-xs">
                <MoonStar aria-hidden="true" className="size-3.5" />
                Runs overnight and ends the next morning.
              </p>
            ) : null}

            <fieldset>
              <legend className="text-xs font-medium">
                Days<span className="text-destructive ml-0.5">*</span>
              </legend>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {WEEKDAYS.map((day) => {
                  const on = draft.days.has(day);
                  return (
                    <button
                      key={day}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleDay(day)}
                      className={cn(
                        "h-8 min-w-11 rounded-md border px-2 text-xs font-medium transition-colors duration-150 ease-out active:scale-[0.97]",
                        on
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border/60 text-muted-foreground hover:bg-muted/40",
                      )}
                    >
                      {WEEKDAY_SHORT[day]}
                    </button>
                  );
                })}
              </div>
              {draft.days.size === 0 ? (
                <p className="text-destructive mt-1 text-xs">Pick at least one day.</p>
              ) : null}
            </fieldset>

            <div className="grid grid-cols-2 gap-3">
              <Field label="From (optional)" htmlFor="slot-from">
                <Input
                  id="slot-from"
                  type="date"
                  value={draft.effectiveFrom}
                  onChange={(e) => set("effectiveFrom", e.target.value)}
                />
              </Field>
              <Field label="Until (optional)" htmlFor="slot-until">
                <Input
                  id="slot-until"
                  type="date"
                  value={draft.effectiveUntil}
                  onChange={(e) => set("effectiveUntil", e.target.value)}
                />
              </Field>
            </div>
            {!rangeValid ? (
              <p className="text-destructive -mt-2 text-xs">
                The end date must be after the start.
              </p>
            ) : null}

            <Field
              label="Display order"
              htmlFor="slot-order"
              required
              helper="Lower numbers show first in the learner's shift picker."
            >
              <Input
                id="slot-order"
                type="number"
                inputMode="numeric"
                min={0}
                value={draft.displayOrder}
                className="tabular-nums"
                onChange={(e) => set("displayOrder", e.target.value)}
              />
            </Field>

            {isEdit ? (
              <label className="border-border/60 bg-muted/20 flex items-start gap-3 rounded-md border px-3 py-2.5">
                <Switch
                  checked={draft.isActive}
                  onCheckedChange={(v) => set("isActive", v)}
                  aria-label="Active"
                />
                <div className="grid gap-0.5">
                  <span className="text-sm font-medium">Active</span>
                  <span className="text-muted-foreground text-xs">
                    Inactive shifts can't be booked. Existing bookings keep their shift.
                  </span>
                </div>
              </label>
            ) : null}
          </div>

          <div className="border-border/60 bg-card/40 flex items-center justify-end gap-2 border-t px-6 py-4 backdrop-blur">
            <SheetClose asChild>
              <Button variant="ghost" type="button" disabled={submitting}>
                Cancel
              </Button>
            </SheetClose>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? <Loader2 aria-hidden="true" className="size-4 animate-spin" /> : null}
              {isEdit ? "Save changes" : "Add shift"}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Field({
  label,
  htmlFor,
  required,
  helper,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  helper?: string;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-xs">
        {label}
        {required ? <span className="text-destructive ml-0.5">*</span> : null}
      </Label>
      <div className="mt-1">{children}</div>
      {error ? (
        <p id={`${htmlFor}-error`} className="text-destructive mt-1 text-xs">
          {error}
        </p>
      ) : helper ? (
        <p className="text-muted-foreground mt-1 text-xs">{helper}</p>
      ) : null}
    </div>
  );
}
